import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Coins, Building2, TrendingUp, Wallet, ShieldCheck, Clock, XCircle } from "lucide-react";
import { fmt } from "@/lib/format";

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [recent, setRecent] = useState([]);
  const [props, setProps] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/my/portfolio"),
      api.get("/my/bricks"),
      api.get("/transactions"),
      api.get("/properties"),
    ])
      .then(([p, b, t, pp]) => {
        setPortfolio({ ...p.data, bricks: b.data });
        setRecent(t.data.slice(0, 5));
        setProps(pp.data.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  if (!portfolio) return <div className="p-8 text-gray-500">Loading...</div>;

  const stats = [
    { icon: Coins, label: "Bricks owned", value: portfolio.total_bricks, accent: true },
    { icon: Building2, label: "Properties", value: portfolio.unique_properties },
    { icon: Wallet, label: "Invested", value: fmt(portfolio.total_invested) },
    { icon: TrendingUp, label: "Projected yearly", value: fmt(portfolio.projected_annual_roi + portfolio.projected_annual_rental) },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="overline">Welcome back</div>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2">
          {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-gray-400 mt-2">Your fractional real estate portfolio</p>

        <KYCBanner user={user} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`bp-surface rounded-2xl p-5 ${s.accent ? "border-[#D4AF37]/30" : ""}`}
              data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}
            >
              <s.icon className={`w-4 h-4 mb-3 ${s.accent ? "text-[#D4AF37]" : "text-gray-400"}`} />
              <div className="overline text-[10px]">{s.label}</div>
              <div className={`font-heading text-2xl md:text-3xl mt-2 font-light ${s.accent ? "text-[#D4AF37]" : "text-white"}`}>
                {s.value}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Wallet & rental earned */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bp-surface rounded-2xl p-6">
            <div className="overline mb-2">Wallet balance</div>
            <div className="font-heading text-4xl text-[#D4AF37] font-light">
              {fmt(user?.wallet_balance || 0, { decimals: true })}
            </div>
            <div className="text-xs text-gray-500 mt-2">Rental payouts and cash credits</div>
          </div>
          <div className="bp-surface rounded-2xl p-6">
            <div className="overline mb-2">Rental earned</div>
            <div className="font-heading text-4xl text-white font-light">
              {fmt(portfolio.rental_earned, { decimals: true })}
            </div>
            <div className="text-xs text-gray-500 mt-2">Lifetime distributions received</div>
          </div>
        </div>

        {/* Recent activity + featured */}
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <div className="md:col-span-2">
            <div className="flex items-baseline justify-between mb-4">
              <div className="overline">Recent activity</div>
              <Link to="/transactions" className="text-[#D4AF37] text-xs hover:underline">
                View all →
              </Link>
            </div>
            <div className="bp-surface rounded-2xl divide-y divide-white/5">
              {recent.length === 0 ? (
                <div className="p-6 text-gray-500 text-sm">No activity yet</div>
              ) : (
                recent.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-4" data-testid={`tx-row-${t.id}`}>
                    <div>
                      <div className="text-sm text-white capitalize">
                        {t.type.replace("_", " ")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t.property_name} · {new Date(t.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="font-mono text-[#D4AF37]">{fmt(t.amount, { decimals: true })}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="overline mb-4">Explore</div>
            <div className="space-y-3">
              {props.map((p) => (
                <Link key={p.id} to={`/properties/${p.id}`} className="block bp-surface rounded-xl p-4 hover:border-[#D4AF37]/40">
                  <div className="text-white text-sm">{p.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{fmt(p.price_per_brick)}/brick</div>
                </Link>
              ))}
              <Link to="/properties">
                <Button className="btn-gold-outline rounded-full w-full h-10" data-testid="dashboard-browse-btn">
                  Browse all properties
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


function KYCBanner({ user }) {
  if (!user || user.kyc_status === "approved") return null;
  const status = user.kyc_status || "none";
  const config = {
    none: {
      icon: ShieldCheck,
      color: "#D4AF37",
      title: "Verify your identity to start investing",
      subtitle: "One-time KYC check. Takes under 5 minutes.",
      cta: "Complete KYC",
    },
    pending: {
      icon: Clock,
      color: "#F59E0B",
      title: "KYC under review",
      subtitle: "We'll notify you once a compliance officer approves your documents.",
      cta: "View status",
    },
    rejected: {
      icon: XCircle,
      color: "#EF4444",
      title: "KYC rejected — please resubmit",
      subtitle: "Check rejection reason and try again with corrected documents.",
      cta: "Resubmit",
    },
  }[status];
  return (
    <div
      className="mt-6 rounded-2xl p-5 flex flex-wrap gap-4 items-center"
      style={{ background: `${config.color}10`, border: `1px solid ${config.color}40` }}
      data-testid="kyc-banner"
    >
      <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${config.color}20`, border: `1px solid ${config.color}40` }}>
        <config.icon className="w-5 h-5" style={{ color: config.color }} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-heading text-lg text-white">{config.title}</div>
        <div className="text-xs text-gray-400 mt-0.5">{config.subtitle}</div>
      </div>
      <Link to="/kyc">
        <Button className="btn-gold rounded-full h-10 px-6" data-testid="kyc-banner-cta">
          {config.cta}
        </Button>
      </Link>
    </div>
  );
}
